import { reverse } from 'lodash';

import { PortalChargeFragment, PortalPaymentDueFragment } from '@willow/graphql-iso/src/portal';
import {
  getUnfulfilledPaymentsDue,
  GetUnfulfilledPaymentsDueOptions,
  paymentAmountFormat,
  paymentDateFormat,
} from '@willow/shared-web';

export type PaymentFormAmountDueDisplay = { desktop: string; tablet: string };

export type PortalDue = PortalPaymentDueFragment | PortalChargeFragment;

export const paymentFormAmountDuesForDisplay = (portalDues: PortalDue[]): PaymentFormAmountDueDisplay[] =>
  portalDues.flatMap((portalDue) => formatPortalDueItem(portalDue));

export const formatPortalDueItem = (dueItem: PortalDue): PaymentFormAmountDueDisplay[] => {
  switch (dueItem.__typename) {
    case 'PaymentDue':
      return formatPaymentDueItem(dueItem);
    case 'Advance':
    case 'Fee':
      return formatChargeDueItem(dueItem);
    default:
      // assertNever does not like dueItem or dueItem.__typename
      return [];
  }
};

// Statements are shown as billed: the spread portion each one carries stays on it, and the upfront
// amount shown alongside covers only the portions not yet billed
const formatPaymentDueItem = (paymentDue: PortalPaymentDueFragment): PaymentFormAmountDueDisplay[] => {
  const { paymentDate, status } = paymentDue;
  const amount = status.amountRemainingFromBorrower;

  return [
    {
      desktop: `${paymentAmountFormat(amount)} (due ${paymentDateFormat(paymentDate)})`,
      tablet: `${paymentAmountFormat(amount)} (due ${paymentDateFormat(paymentDate, true)})`,
    },
  ];
};

const formatChargeDueItem = (charge: PortalChargeFragment): PaymentFormAmountDueDisplay[] => {
  const { amountRemaining } = charge;
  const description = charge.__typename === 'Fee' ? 'fee' : 'advance';
  const chargeDescription = `${paymentAmountFormat(amountRemaining)} (${description})`;

  return [{ desktop: chargeDescription, tablet: chargeDescription }];
};

export const getPaymentFormAmountDueDisplay = (
  paymentsDue: PortalPaymentDueFragment[],
  chargesDue: PortalChargeFragment[],
  options: GetUnfulfilledPaymentsDueOptions,
): PortalDue[] => {
  // Sort the Payments + Fees from oldest to newest
  const orderedPaymentsDue = getUnfulfilledPaymentsDue(paymentsDue, options);
  const orderedChargesDue = reverse([...chargesDue]);

  // Format the items for display with the charges appearing last
  return [...orderedPaymentsDue, ...orderedChargesDue];
};
